// LEGAL_REVIEW_REQUIRED: These pages are comprehensive, business-ready TEMPLATES drafted to read like a
// large platform's policies (binding arbitration, class-action waiver, liability caps, indemnification,
// independent-contractor framing). They are NOT a substitute for review by a licensed attorney in the
// company's jurisdiction before launch. Fill the [BRACKETED] items (state of organization, registered
// address) and have counsel confirm the arbitration/governing-law clauses for your states of operation.

export const COMPANY = 'BestHand';
export const LEGAL_ENTITY = 'BestHand US LLC';
export const ADDRESS = '502 W 7th St, Ste 100, Erie, PA 16502';
export const CONTACT = {
  support: 'support@besthandapp.com',
  legal: 'contact@besthandapp.com',
  phone: '+1 (267) 215-1234',
  phoneRaw: '2672151234',
};
// LEGAL_REVIEW_REQUIRED: have counsel confirm the governing-law state + arbitration venue for your
// states of operation. Company is organized/located in Erie County, Pennsylvania.
export const GOVERNING_STATE = 'the Commonwealth of Pennsylvania';
export const ARBITRATION_VENUE = 'Erie County, Pennsylvania';

const intro = (t) => t;

const PAGES = {
  // ───────────────────────────────────────── TERMS OF SERVICE ─────────────────────────────────────────
  terms: {
    url: '/terms',
    eyebrow: 'Legal',
    title: 'Terms of Service',
    metaTitle: 'Terms of Service — BestHand',
    metaDescription:
      'The terms governing your use of the BestHand platform, including payments, cancellations, disclaimers, and binding individual arbitration.',
    updated: 'June 5, 2026',
    intro:
      'These Terms of Service ("Terms") are a binding contract between you and ' +
      LEGAL_ENTITY +
      ' ("BestHand," "we," "us," or "our"), located at ' +
      ADDRESS +
      ', and govern your access to and use of the BestHand website, mobile applications, and services (together, the "Platform"). Please read them carefully — Section 16 requires that most disputes be resolved by binding individual arbitration and waives your right to a jury trial and to participate in class actions.',
    sections: [
      {
        id: 'acceptance',
        heading: '1. Acceptance of these Terms',
        body: [
          'By creating an account, booking a job, offering services, clicking "I agree," or otherwise accessing or using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy, which is incorporated by reference. If you do not agree, you must not access or use the Platform.',
          'If you use the Platform on behalf of a company or other entity, you represent that you are authorized to bind that entity, and "you" refers to that entity.',
        ],
      },
      {
        id: 'platform-role',
        heading: '2. What BestHand is — and is not',
        body: [
          'BestHand is solely a technology platform and online marketplace. Our only role is to connect customers ("Customers") who seek home and local services with independent service professionals ("Pros") who offer them, and to facilitate booking and payment. BestHand is a neutral venue and an intermediary — nothing more.',
          { h: 'BestHand performs no services' },
          'BestHand does not provide, perform, supervise, schedule, control, or participate in any home, repair, cleaning, moving, installation, or other service of any kind. We are not a contractor, builder, handyman, tradesperson, employer, or service provider. We do not employ Pros and are not a party to the agreement for services, which is formed and exists solely and directly between a Customer and a Pro.',
          { h: 'We do not control Pros and are not responsible for them' },
          'Pros are independent contractors — not employees, agents, representatives, partners, or joint venturers of BestHand. We do not set, direct, supervise, or control how, when, where, or whether a Pro performs any work. To the maximum extent permitted by law, BestHand is not responsible or liable for the acts, omissions, conduct, decisions, work product, quality, timeliness, pricing, tools, licensing, insurance, legality, or safety of any Pro or Customer, or for any property damage, personal injury, loss, or dispute arising from any service or interaction.',
          { h: 'No endorsement, screening, or guarantee' },
          'Any verification, background check, rating, review, badge, tier, or other information shown on the Platform is provided for convenience only and is not a recommendation, endorsement, warranty, or guarantee of any Pro, Customer, or service. BestHand does not guarantee that any Pro is suitable, qualified, licensed, insured, available, or that any service will be performed to your satisfaction or at all.',
          { h: 'You use the Platform and engage other users at your own risk' },
          'You acknowledge and agree that you select, hire, accept, and interact with other users knowingly, voluntarily, in full awareness, and at your own sole risk and discretion. You are solely responsible for evaluating, and you assume all risks arising from, any Pro, Customer, service, communication, or interaction. To the fullest extent permitted by law, you waive and release BestHand and its affiliates, officers, directors, employees, and agents from any and all claims, demands, liabilities, damages, and expenses, known or unknown, arising out of or relating to other users, the services they provide or receive, and your decision to use, hire, or rely on them (see also Sections 12, 13, and 15).',
          { h: 'No professional, agency, or employment relationship' },
          'Nothing on the Platform creates an employment, agency, fiduciary, partnership, or joint-venture relationship between you and BestHand.',
        ],
      },
      {
        id: 'eligibility',
        heading: '3. Eligibility & accounts',
        body: [
          'You must be at least 18 years old and able to form a binding contract to use the Platform. You agree to provide accurate, current, and complete information and to keep it updated.',
          'You are responsible for all activity under your account and for keeping your credentials confidential. Notify us immediately at ' +
            CONTACT.support +
            ' of any unauthorized use. We may refuse, suspend, or terminate accounts at our discretion, including for conduct we believe violates these Terms or harms the Platform, other users, or third parties.',
        ],
      },
      {
        id: 'bookings',
        heading: '4. Bookings, estimates & the Customer–Pro agreement',
        body: [
          'When a Customer books a job, the Customer and the Pro form a direct agreement for the requested services. Prices, estimates, and time windows shown on the Platform are provided by Pros or generated from Pro-supplied rates and are estimates only; final amounts may vary based on the actual work performed, materials, additional workers, multi-day jobs, and on-site conditions, as disclosed in the app.',
          'A travel or visit fee may apply and is retained by the Platform. Service fees are charged as a percentage and are disclosed before you confirm. You authorize the amounts presented to you at and after booking, including approved additions made during a job.',
        ],
      },
      {
        id: 'payments',
        heading: '5. Payments, holds & authorizations',
        body: [
          'Payments are processed by our third-party payment processor, Stripe, Inc. By making or receiving payments, you also agree to the Stripe Connected Account Agreement and Stripe Services Agreement. BestHand does not store full card numbers.',
          'You authorize BestHand and Stripe to place a hold on, charge, and capture your payment method for booked services, approved mid-job additions, materials, additional workers, multi-day work, applicable taxes, and any fees described in the app. If a charge fails, you remain responsible for the amount owed, and we may suspend your account or use a payment processor to recover it.',
          { h: 'Pro payouts' },
          'Pros are paid out through Stripe, subject to identity verification and payout conditions. BestHand may withhold, offset, or reverse payouts to address chargebacks, refunds, disputes, fraud, or violations of these Terms.',
        ],
      },
      {
        id: 'cancellations',
        heading: '6. Cancellations, reschedules & refunds',
        body: [
          'Cancellation, reschedule, and refund rights are described in our Refund & Cancellation Policy, which is part of these Terms. Cancellations made more than 24 hours before a job’s scheduled start are fully refunded. Cancellations made within 24 hours of the scheduled start are charged one (1) hour at the dispatched Pro’s agreed hourly rate, and a visit/travel fee may also apply once a Pro has been dispatched or begun travel. Reschedules within the windows described in the app are, in many cases, free.',
          'Refunds, where due, are issued to the original payment method. BestHand may, in its sole discretion and without admitting liability, issue credits or refunds to resolve disputes.',
        ],
      },
      {
        id: 'conduct',
        heading: '7. User conduct & prohibited uses',
        body: [
          'You agree not to:',
          {
            list: [
              'Violate any law or regulation, or infringe the rights of others;',
              'Circumvent the Platform to arrange or pay for services off-platform in order to avoid fees ("disintermediation");',
              'Provide false, misleading, or fraudulent information, or impersonate any person or entity;',
              'Harass, threaten, discriminate against, or harm any user, Pro, or third party;',
              'Post unlawful, defamatory, infringing, obscene, or deceptive content, including fake reviews;',
              'Introduce malware, scrape, reverse-engineer, overload, or interfere with the Platform or its security;',
              'Use the Platform for any illegal, unsafe, or unauthorized purpose.',
            ],
          },
          'We may investigate and take any action we deem appropriate, including removing content, suspending accounts, and reporting to authorities.',
        ],
      },
      {
        id: 'pros',
        heading: '8. Additional terms for Pros',
        body: [
          'If you offer services as a Pro, you represent and warrant that you are legally able to perform the services, hold all licenses, permits, insurance, and registrations required by law, and will perform services in a professional, safe, and lawful manner. You are solely responsible for your taxes, tools, expenses, and compliance, and for any helpers or additional workers you bring.',
          'You authorize BestHand and its vendors to conduct identity verification and background checks as described in our Background Check Policy. You acknowledge you are an independent contractor and not an employee, and you assume all risks of performing services. See our Independent Contractor terms.',
        ],
      },
      {
        id: 'content',
        heading: '9. Your content, reviews & feedback',
        body: [
          'You retain ownership of content you submit (photos, reviews, messages), but you grant BestHand a worldwide, non-exclusive, royalty-free, sublicensable license to host, use, reproduce, display, and distribute that content to operate, promote, and improve the Platform. You represent that you have the rights to grant this license and that your content is accurate and lawful.',
          'Reviews must reflect a genuine experience. We may remove content that violates these Terms, but we are not obligated to monitor or store content.',
        ],
      },
      {
        id: 'ip',
        heading: '10. Intellectual property',
        body: [
          'The Platform, including its software, design, text, graphics, logos, and the BestHand marks, is owned by BestHand or its licensors and protected by intellectual-property laws. We grant you a limited, revocable, non-exclusive, non-transferable license to use the Platform for its intended purpose. You may not copy, modify, distribute, sell, or create derivative works except as expressly permitted.',
        ],
      },
      {
        id: 'third-party',
        heading: '11. Third-party services',
        body: [
          'The Platform integrates third-party services (for example, Stripe for payments, mapping, communications, and background-check vendors). Your use of those services may be subject to their own terms, and BestHand is not responsible for third-party services or content.',
        ],
      },
      {
        id: 'disclaimers',
        heading: '12. Disclaimers',
        body: [
          'THE PLATFORM AND ALL SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.',
          'BESTHAND DOES NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT ANY PRO OR CUSTOMER, OR THE QUALITY, SAFETY, OR LEGALITY OF ANY SERVICE, WILL MEET YOUR EXPECTATIONS. YOU USE THE PLATFORM AND ENGAGE WITH OTHER USERS AT YOUR OWN RISK. BestHand does not guarantee the work, conduct, or outcomes of any Pro or Customer.',
        ],
      },
      {
        id: 'liability',
        heading: '13. Limitation of liability',
        body: [
          'TO THE MAXIMUM EXTENT PERMITTED BY LAW, BESTHAND AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, DATA, GOODWILL, OR PROPERTY, ARISING OUT OF OR RELATING TO THE PLATFORM OR ANY SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.',
          'TO THE MAXIMUM EXTENT PERMITTED BY LAW, BESTHAND’S TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS RELATING TO THE PLATFORM WILL NOT EXCEED THE GREATER OF (A) THE TOTAL FEES BESTHAND RETAINED FROM TRANSACTIONS GIVING RISE TO THE CLAIM IN THE SIX (6) MONTHS BEFORE THE EVENT, OR (B) ONE HUNDRED U.S. DOLLARS ($100). Some jurisdictions do not allow certain limitations, so some of the above may not apply to you.',
        ],
      },
      {
        id: 'indemnification',
        heading: '14. Indemnification',
        body: [
          'You agree to defend, indemnify, and hold harmless BestHand and its affiliates, officers, directors, employees, and agents from and against any claims, damages, liabilities, losses, and expenses (including reasonable attorneys’ fees) arising out of or related to: (a) your use of the Platform; (b) your violation of these Terms or any law; (c) your content; (d) the services you provide or receive; or (e) your interactions with any other user.',
        ],
      },
      {
        id: 'assumption-risk',
        heading: '15. Assumption of risk & release',
        body: [
          'Home and local services may involve risk. To the maximum extent permitted by law, you assume all risks arising from your use of the Platform and your interactions with other users, and you release BestHand from claims, demands, and damages arising out of or related to disputes between users, the conduct or work of any Pro or Customer, or property damage or personal injury occurring in connection with services.',
        ],
      },
      {
        id: 'arbitration',
        heading: '16. Dispute resolution — binding individual arbitration & class-action waiver',
        body: [
          {
            note:
              'PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS. IT REQUIRES YOU AND BESTHAND TO RESOLVE DISPUTES THROUGH BINDING INDIVIDUAL ARBITRATION, AND IT WAIVES YOUR RIGHT TO A JURY TRIAL AND TO PARTICIPATE IN ANY CLASS, COLLECTIVE, OR REPRESENTATIVE ACTION.',
          },
          { h: '16.1 Agreement to arbitrate' },
          'You and BestHand agree that any dispute, claim, or controversy arising out of or relating to these Terms, the Platform, any services, or your relationship with BestHand — whether based in contract, tort, statute, fraud, misrepresentation, or any other legal theory, and whether arising before, during, or after termination — will be resolved exclusively by final and binding arbitration on an individual basis, and not in a court of general jurisdiction. This agreement is governed by the Federal Arbitration Act (9 U.S.C. § 1 et seq.).',
          { h: '16.2 No court & no jury' },
          'Except for the limited exceptions below, you and BestHand each waive the right to sue in court and to have a trial before a judge or jury. Arbitration uses different rules than court and is subject to limited review.',
          { h: '16.3 Class-action & representative-action waiver' },
          'YOU AND BESTHAND AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, COLLECTIVE, CONSOLIDATED, PRIVATE-ATTORNEY-GENERAL, OR REPRESENTATIVE PROCEEDING. The arbitrator may not consolidate more than one person’s claims and may not preside over any form of a representative or class proceeding. If this specific waiver is found unenforceable as to a particular claim, then that claim (and only that claim) shall be severed and brought in court, while all other claims remain in arbitration.',
          { h: '16.4 Rules, forum & costs' },
          'The arbitration will be administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules, as modified by these Terms. If the AAA is unavailable, the parties will agree on, or a court will appoint, another administrator. The arbitration will take place in ' +
            ARBITRATION_VENUE +
            ', or, at your election, by telephone, video, or written submissions, or in the U.S. county where you reside. The arbitrator’s award may be entered in any court with jurisdiction. BestHand will pay arbitration filing and administrative fees to the extent required by the applicable rules or law for claims under the threshold set by those rules.',
          { h: '16.5 Delegation' },
          'The arbitrator, and not any court, has exclusive authority to resolve any dispute relating to the interpretation, applicability, enforceability, or formation of this arbitration agreement, including any claim that all or part of it is void or voidable — except that a court decides the enforceability of the class-action waiver in Section 16.3.',
          { h: '16.6 Exceptions' },
          'Either party may (a) bring an individual claim in small-claims court if it qualifies, and (b) seek injunctive or equitable relief in court to protect intellectual property or stop unauthorized access. These exceptions do not waive the rest of this Section.',
          { h: '16.7 30-day right to opt out' },
          'You may opt out of this arbitration agreement within thirty (30) days of first accepting these Terms by emailing ' +
            CONTACT.legal +
            ' with your name, the email/phone on your account, and a clear statement that you opt out of arbitration. Opting out will not affect any other part of these Terms. If you do not opt out within 30 days, you agree to be bound by this Section.',
          { h: '16.8 Survival' },
          'This Section survives termination of your account and these Terms.',
        ],
      },
      {
        id: 'governing-law',
        heading: '17. Governing law & forum',
        body: [
          'These Terms and any dispute not subject to arbitration are governed by the laws of ' +
            GOVERNING_STATE +
            ', without regard to conflict-of-laws rules, and by applicable U.S. federal law. For any matter not subject to arbitration, you and BestHand consent to the exclusive jurisdiction and venue of the state and federal courts located in ' +
            ARBITRATION_VENUE +
            ', and waive any objection to that forum.',
        ],
      },
      {
        id: 'termination',
        heading: '18. Suspension & termination',
        body: [
          'You may stop using the Platform at any time. We may suspend or terminate your access at any time, with or without notice, including for violations of these Terms, risk to users, or unlawful activity. Sections that by their nature should survive termination (including Sections 9–18 and 19) will survive.',
        ],
      },
      {
        id: 'changes',
        heading: '19. Changes to these Terms & to the Platform',
        body: [
          'We may modify these Terms from time to time. If we make material changes, we will provide notice (for example, by posting the updated Terms with a new "Last updated" date or by in-app or email notice). Changes are effective when posted unless stated otherwise. Your continued use of the Platform after changes take effect constitutes acceptance. We may also change, suspend, or discontinue any part of the Platform at any time.',
        ],
      },
      {
        id: 'misc',
        heading: '20. General',
        body: [
          { h: 'Entire agreement' },
          'These Terms, the Privacy Policy, the Refund & Cancellation Policy, and any policies referenced here are the entire agreement between you and BestHand regarding the Platform and supersede prior agreements.',
          { h: 'Severability & no waiver' },
          'If any provision is found unenforceable, it will be limited or severed to the minimum extent necessary, and the remaining provisions remain in effect. Our failure to enforce a provision is not a waiver.',
          { h: 'Assignment' },
          'You may not assign these Terms without our consent. We may assign them, including in connection with a merger, acquisition, or sale of assets.',
          { h: 'Force majeure' },
          'BestHand is not liable for failures or delays caused by events beyond its reasonable control.',
          { h: 'Notices & contact' },
          'We may provide notices to you via the app, email, or SMS. You may contact us at ' +
            CONTACT.legal +
            ', ' +
            CONTACT.phone +
            ', or by mail to ' +
            LEGAL_ENTITY +
            ', ' +
            ADDRESS +
            '.',
        ],
      },
    ],
  },

  // ───────────────────────────────────────── PRIVACY POLICY ─────────────────────────────────────────
  'privacy-policy': {
    url: '/privacy-policy',
    eyebrow: 'Legal',
    title: 'Privacy Policy',
    metaTitle: 'Privacy Policy — BestHand',
    metaDescription:
      'How BestHand collects, uses, shares, and protects your personal information, and the choices and rights you have.',
    updated: 'June 5, 2026',
    intro:
      'This Privacy Policy explains how ' +
      LEGAL_ENTITY +
      ' ("BestHand"), located at ' +
      ADDRESS +
      ', collects, uses, shares, and protects personal information when you use our website, apps, and services. By using the Platform, you agree to this Policy.',
    sections: [
      {
        id: 'collect',
        heading: '1. Information we collect',
        body: [
          { h: 'Information you provide' },
          'Account details (name, email, phone, password), profile information, addresses and job details, photos and messages, reviews, and payment information you submit (processed by Stripe; we do not store full card numbers).',
          { h: 'Information collected automatically' },
          'Device and usage data (IP address, device identifiers, app version, pages viewed), and, with your permission, approximate or precise location to match you with nearby jobs or Pros.',
          { h: 'Information from third parties' },
          'Identity-verification and background-check results for Pros (via our vendors), and information from payment, mapping, and communications providers.',
        ],
      },
      {
        id: 'use',
        heading: '2. How we use information',
        body: [
          {
            list: [
              'Operate the Platform — create accounts, match Customers and Pros, process bookings and payments;',
              'Communicate with you, including service messages, receipts, and (with consent) marketing and SMS;',
              'Provide support and resolve disputes, refunds, and chargebacks;',
              'Protect safety and security, prevent fraud, and enforce our Terms;',
              'Verify identity and eligibility, including background checks for Pros;',
              'Improve, personalize, and develop features; and comply with legal obligations.',
            ],
          },
        ],
      },
      {
        id: 'share',
        heading: '3. How we share information',
        body: [
          'We share information: between Customers and Pros as needed to perform a job (for example, name, job location, and contact for an active booking); with service providers acting on our behalf (payments via Stripe, hosting, mapping, communications, analytics, background-check vendors); to comply with law or lawful requests; to protect rights and safety; and in connection with a merger, acquisition, or sale of assets. We do not sell your personal information for money.',
        ],
      },
      {
        id: 'sms',
        heading: '4. SMS & communications',
        body: [
          'If you opt in to SMS, we send transactional and, with consent, promotional messages. Message and data rates may apply. You can opt out at any time by replying STOP. See our SMS Consent page for details. Opting out of marketing does not stop essential service messages.',
        ],
      },
      {
        id: 'cookies',
        heading: '5. Cookies & tracking',
        body: [
          'We and our partners use cookies and similar technologies to keep you signed in, remember preferences, measure performance, and improve the Platform. You can control cookies through your browser settings; some features may not work without them.',
        ],
      },
      {
        id: 'retention',
        heading: '6. Data retention',
        body: [
          'We keep personal information for as long as needed to provide the Platform, comply with legal, tax, and accounting obligations, resolve disputes, and enforce agreements. We then delete or de-identify it.',
        ],
      },
      {
        id: 'security',
        heading: '7. Security',
        body: [
          'We use technical and organizational measures designed to protect personal information, including encryption in transit and tokenized payments via Stripe. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.',
        ],
      },
      {
        id: 'rights',
        heading: '8. Your choices & rights',
        body: [
          'Depending on where you live (for example, California or other U.S. states), you may have rights to access, correct, delete, or receive a copy of your personal information, and to opt out of certain sharing or targeted advertising. To exercise these rights, contact ' +
            CONTACT.legal +
            '. We will not discriminate against you for exercising your rights. You may also update most information in your account settings.',
        ],
      },
      {
        id: 'children',
        heading: '9. Children’s privacy',
        body: [
          'The Platform is not directed to children under 18, and we do not knowingly collect their personal information. If you believe a child has provided us information, contact us and we will delete it.',
        ],
      },
      {
        id: 'large-projects',
        // LEGAL_REVIEW_REQUIRED: Large Projects / business-account data disclosure.
        heading: '10. Large Projects & business (Company) accounts',
        body: [
          'If you use BestHand Large Projects as a business ("Company"), we collect and process additional information to verify the business and enable payouts. This may include your legal business name, EIN/tax ID, business address, beneficial-owner and representative identity details (including government identifiers and ID documents), licensing, insurance and bond information, and bank/payout details.',
          'We use third-party providers to collect and verify this information and to process payments and payouts — primarily Stripe (identity/KYC, Connect payouts, and the bank account) and Middesk (business verification, e.g., name/TIN match, Secretary-of-State and watchlist screening). Information is shared with these providers as needed to verify your business and move funds, and their handling is governed by their own privacy policies.',
          'For each project we also store the documents generated through the Platform — quotes, the Home Improvement Contract, change orders, lien waivers, and payout records — and share relevant details with the Customer and, where applicable, the Company’s subcontractors, to perform the project. We retain these records as required by law (for example, tax, contractor, and lien recordkeeping).',
          'Business-account and project records may be retained after account closure where required for legal, tax, accounting, audit, dispute, or fraud-prevention purposes.',
        ],
      },
      {
        id: 'changes',
        heading: '11. Changes & contact',
        body: [
          'We may update this Policy and will revise the "Last updated" date. Material changes may be communicated in-app or by email. Questions? Contact ' +
            CONTACT.legal +
            ' or ' +
            CONTACT.phone +
            '.',
        ],
      },
    ],
  },

  // ───────────────────────────────────── REFUND & CANCELLATION ─────────────────────────────────────
  refunds: {
    url: '/refunds',
    eyebrow: 'Policies',
    title: 'Refund & Cancellation Policy',
    metaTitle: 'Refund & Cancellation Policy — BestHand',
    metaDescription: 'How cancellations, reschedules, visit fees, and refunds work on BestHand.',
    updated: 'June 5, 2026',
    intro:
      'This Policy explains how cancellations, reschedules, and refunds work. It is part of our Terms of Service.',
    sections: [
      {
        id: 'reschedule',
        heading: 'Rescheduling',
        body: [
          'Customers can reschedule a booking from the app. Within the windows shown at booking, reschedules are typically free. Reschedules requested very close to the start time, or after a Pro has been dispatched, may be treated as a late cancellation.',
        ],
      },
      {
        id: 'cancellation',
        heading: 'Cancellations & visit fees',
        body: [
          'You may cancel a booking at any time from the app. Your cancellation charge, if any, depends on how far in advance you cancel relative to the job’s scheduled start time:',
          {
            list: [
              'More than 24 hours before the scheduled start — you receive a full refund, at no charge.',
              'Within 24 hours of the scheduled start — you are charged one (1) hour at the agreed hourly rate of the Pro dispatched for your job, to compensate the Pro for the time reserved for you.',
            ],
          },
          'A visit/travel fee may also apply where a Pro has already been dispatched or begun travel to your location. The exact amount is always shown in the app before you confirm a cancellation.',
        ],
      },
      {
        id: 'refunds',
        heading: 'Refunds',
        body: [
          'If you were charged for services not performed, or a job was not completed as agreed, you may request a refund through the app or by contacting ' +
            CONTACT.support +
            '. Approved refunds are issued to your original payment method and may take several business days to appear. BestHand may, at its discretion and without admitting fault, issue credits or partial refunds to resolve a dispute.',
        ],
      },
      {
        id: 'disputes',
        heading: 'Disputes & chargebacks',
        body: [
          'Please contact us before initiating a chargeback so we can help. Fraudulent or abusive refund or chargeback activity may result in account suspension and recovery of amounts owed.',
        ],
      },
    ],
  },

  // ─────────────────────────────────── INDEPENDENT CONTRACTOR ───────────────────────────────────
  'independent-contractor': {
    url: '/independent-contractor',
    eyebrow: 'For Pros',
    title: 'Independent Contractor Terms',
    metaTitle: 'Independent Contractor Terms — BestHand',
    metaDescription:
      'The independent-contractor relationship between Pros and BestHand: no employment, your own schedule, your own taxes and tools.',
    updated: 'June 5, 2026',
    intro:
      'These terms describe the relationship between service professionals ("Pros") and BestHand. They supplement our Terms of Service.',
    sections: [
      {
        id: 'status',
        heading: 'Independent-contractor status',
        body: [
          'Pros are independent contractors, not employees, agents, partners, or joint venturers of BestHand. Nothing in our agreements creates an employment relationship. Pros decide whether, when, and which jobs to accept, and are free to provide services through other channels.',
        ],
      },
      {
        id: 'control',
        heading: 'Your business, your control',
        body: [
          {
            list: [
              'You set your availability and accept or decline jobs at your discretion;',
              'You provide your own tools, equipment, transportation, and any helpers;',
              'You control how you perform the work, consistent with the Customer’s request and the law;',
              'You are responsible for your own business expenses.',
            ],
          },
        ],
      },
      {
        id: 'taxes',
        heading: 'Taxes & compliance',
        body: [
          'You are solely responsible for reporting and paying all taxes on your earnings and for obtaining any licenses, permits, registrations, and insurance required by law. BestHand does not withhold taxes. You may receive tax forms (such as a 1099) as required.',
        ],
      },
      {
        id: 'no-benefits',
        heading: 'No employee benefits',
        body: [
          'As an independent contractor, you are not entitled to employee benefits such as workers’ compensation, unemployment, health insurance, or paid leave from BestHand.',
        ],
      },
      {
        id: 'verification',
        heading: 'Verification & standards',
        body: [
          'You agree to identity verification and background checks (see our Background Check Policy) and to perform services lawfully, safely, and professionally. Failure to meet Platform standards may result in suspension or removal.',
        ],
      },
    ],
  },

  // ─────────────────────────────────────── BACKGROUND CHECK ───────────────────────────────────────
  'background-check': {
    url: '/background-check',
    eyebrow: 'Trust & Safety',
    title: 'Background Check Policy',
    metaTitle: 'Background Check Policy — BestHand',
    metaDescription: 'How BestHand verifies Pros through identity and background checks.',
    updated: 'June 5, 2026',
    intro:
      'Trust is the foundation of BestHand. This Policy explains how we verify the professionals on our Platform.',
    sections: [
      {
        id: 'what',
        heading: 'What we check',
        body: [
          // COMPLIANCE: background-check claim intentional (founder confirms). LEGAL_REVIEW_REQUIRED. Present-tense
          // assertion that criminal-records screening is run on every Pro before they accept jobs.
          'Before a Pro can accept jobs, we verify their identity, and Pros complete a verification step as part of onboarding. We may, to the extent permitted by law and using third-party vendors, screen background records, including criminal-records checks consistent with applicable federal, state, and local laws, including the Fair Credit Reporting Act (FCRA) where it applies.',
        ],
      },
      {
        id: 'consent',
        heading: 'Consent & fairness',
        body: [
          'Where a background check is performed, Pros provide consent beforehand. We apply any screening criteria consistently and in compliance with anti-discrimination and "ban-the-box"/fair-chance laws where applicable. A background check is one safeguard and is not a guarantee of any individual’s future conduct.',
        ],
      },
      {
        id: 'limits',
        heading: 'Important limitations',
        body: [
          'No screening process is perfect. BestHand does not guarantee the accuracy or completeness of third-party records, and verification does not make BestHand responsible for a Pro’s conduct. Always use the in-app tools, communicate through the Platform, and report concerns to ' +
            CONTACT.support +
            '.',
        ],
      },
    ],
  },

  // ─────────────────────────────────── PEACE OF MIND PROTECTION ───────────────────────────────────
  'peace-of-mind-protection': {
    url: '/peace-of-mind-protection',
    eyebrow: 'Trust & Safety',
    title: 'Peace of Mind Protection',
    metaTitle: 'Peace of Mind Protection — BestHand',
    metaDescription:
      'The protections that back every BestHand booking: verified pros, secure payments, and a satisfaction commitment.',
    updated: 'June 5, 2026',
    intro:
      'Every job booked and paid through BestHand comes with protections designed to give you peace of mind.',
    sections: [
      {
        id: 'verified',
        heading: 'Verified professionals',
        body: [
          'Pros complete identity verification and background screening before they can accept jobs. Ratings and reviews from real Customers help you choose with confidence.',
        ],
      },
      {
        id: 'secure',
        heading: 'Secure, on-platform payments',
        body: [
          'Payments are processed securely by Stripe and held until work is done. Keeping payments and messages on the Platform protects you and ensures you’re covered by our policies — never pay off-platform.',
        ],
      },
      {
        id: 'satisfaction',
        heading: 'Satisfaction commitment',
        body: [
          'If a job isn’t completed as agreed, contact us and our support team will work with you and the Pro toward a fair resolution, which may include a reschedule, credit, or refund as described in our Refund & Cancellation Policy.',
        ],
      },
      {
        id: 'scope',
        heading: 'Scope & limitations',
        body: [
          'Protections apply only to eligible jobs booked and paid through the Platform and are subject to our Terms of Service, including disclaimers and limitation of liability. This page describes a service commitment, not an insurance policy.',
        ],
      },
    ],
  },

  // ─────────────────────────────────────── SMS CONSENT ───────────────────────────────────────
  'sms-consent': {
    url: '/sms-consent',
    eyebrow: 'Legal',
    title: 'SMS / Text Messaging Terms',
    metaTitle: 'SMS Consent & Terms — BestHand',
    metaDescription: 'How BestHand uses text messages, your consent, and how to opt out.',
    updated: 'June 5, 2026',
    intro: 'These terms describe BestHand’s text-messaging (SMS) program and your consent and choices.',
    sections: [
      {
        id: 'consent',
        heading: 'Your consent',
        body: [
          'By providing your mobile number and opting in, you consent to receive text messages from BestHand, including account, booking, verification (one-time codes), payment, and support messages, and — where you opt in — promotional messages. Consent is not a condition of purchase for marketing messages.',
        ],
      },
      {
        id: 'rates',
        heading: 'Message frequency & rates',
        body: [
          'Message frequency varies based on your activity. Message and data rates may apply according to your mobile plan. BestHand is not responsible for carrier charges.',
        ],
      },
      {
        id: 'optout',
        heading: 'Opt out & help',
        body: [
          'Reply STOP to any message to opt out of that category of messages, or HELP for help. You may continue to receive essential transactional messages needed to operate your account and bookings. You can also contact ' +
            CONTACT.support +
            '.',
        ],
      },
      {
        id: 'privacy',
        heading: 'Privacy',
        body: [
          'We do not sell your mobile number. Your information is handled per our Privacy Policy. Carriers are not liable for delayed or undelivered messages.',
        ],
      },
    ],
  },

  // ─────────────────────────────────────── ABOUT ───────────────────────────────────────
  'about-besthand': {
    url: '/about-besthand',
    eyebrow: 'Company',
    title: 'About BestHand',
    metaTitle: 'About BestHand',
    // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
    metaDescription:
      'BestHand connects homeowners with trusted, background-checked local pros — booked in minutes, backed by secure payments and a satisfaction commitment.',
    updated: 'June 5, 2026',
    intro:
      'BestHand is a modern marketplace that connects customers with trusted, independent local professionals for everyday home services — from plumbing and electrical to cleaning, moving, and more.',
    sections: [
      {
        id: 'mission',
        heading: 'Our mission',
        body: [
          'We believe getting help at home should be simple, transparent, and trustworthy. BestHand makes it easy to find a vetted pro, see upfront pricing, book in minutes, and pay securely — while giving skilled professionals a better way to grow their business on their own terms.',
        ],
      },
      {
        id: 'what',
        heading: 'What makes BestHand different',
        body: [
          {
            // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
            list: [
              'background-checked pros who can be at your door fast;',
              'Upfront, transparent pricing — you pay only for time you approve;',
              'Secure payments powered by Stripe, held until the job is done;',
              'A satisfaction commitment and real reviews from real customers;',
              'A special assistance program supporting seniors and aging veterans.',
            ],
          },
        ],
      },
      {
        id: 'community',
        heading: 'Built for our community',
        body: [
          'BestHand is proud to support the people who keep our neighborhoods running — the homeowners who trust us and the independent pros who power the Platform. We’re committed to fairness, safety, and opportunity for everyone on BestHand.',
        ],
      },
      {
        id: 'contact',
        heading: 'Get in touch',
        body: [
          'Questions, press, or partnership ideas? Reach us at ' + CONTACT.legal + ' or ' + CONTACT.phone + '.',
        ],
      },
    ],
  },

  // ─────────────────────────────────────── HOW IT WORKS ───────────────────────────────────────
  'how-it-works': {
    url: '/how-it-works',
    eyebrow: 'Getting started',
    title: 'How BestHand works',
    metaTitle: 'How It Works — BestHand',
    metaDescription:
      'Book a trusted local pro in three simple steps: tell us what you need, get matched, and relax while the job gets done.',
    updated: 'June 5, 2026',
    intro: 'Getting help at home takes just a minute. Here’s how BestHand works for customers and for pros.',
    sections: [
      {
        id: 'customers',
        heading: 'For customers',
        body: [
          { h: '1. Tell us what you need' },
          'Pick a service and describe your job in under a minute. Add photos and a time that works for you.',
          { h: '2. Get matched with a pro' },
          // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
          'We match you with verified, ID-checked pros nearby. Compare profiles, ratings, and upfront pricing.',
          { h: '3. Book, track & relax' },
          'Confirm your booking, track your pro, chat in-app, and pay securely when the job is done right.',
        ],
      },
      {
        id: 'pricing',
        heading: 'Transparent pricing',
        body: [
          'You see pricing before you confirm and pay only for time you approve. A visit fee may apply and is shown upfront. Payments are processed securely by Stripe and protected until the work is complete.',
        ],
      },
      {
        id: 'pros',
        heading: 'For pros',
        body: [
          // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
          'Sign up, complete a quick verification step, and start accepting jobs near you. Work on your own schedule, get paid fast, and grow your business with reviews and performance tiers. Learn more on our Become a Pro page.',
        ],
      },
      {
        id: 'support',
        heading: 'We’ve got your back',
        body: [
          'Every booking is backed by verified pros, secure payments, and a satisfaction commitment. Need help? Contact ' +
            CONTACT.support +
            '.',
        ],
      },
    ],
  },

  // ─────────────────────────────────────── TRUST & SAFETY ───────────────────────────────────────
  'trust-and-safety': {
    url: '/trust-and-safety',
    eyebrow: 'Trust & Safety',
    title: 'Trust & Safety',
    metaTitle: 'Trust & Safety — BestHand',
    metaDescription:
      'How BestHand keeps the community safe: verified pros, secure payments, on-platform protections, and 24/7 support.',
    updated: 'June 5, 2026',
    intro:
      'Your safety and trust come first. Here’s how we work to protect every customer and pro on BestHand.',
    sections: [
      {
        id: 'verified',
        // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
        heading: 'Verified pros',
        body: [
          'Pros complete identity verification before they can accept jobs. See our Background Check Policy for details on additional screening.',
        ],
      },
      {
        id: 'payments',
        heading: 'Secure payments & on-platform protection',
        body: [
          'Payments are processed by Stripe and held until the job is done. Keeping payments and communication on the Platform protects you and keeps you covered by our policies. Never pay or move conversations off-platform.',
        ],
      },
      {
        id: 'standards',
        heading: 'Community standards',
        body: [
          'We expect respectful, lawful, and non-discriminatory conduct from everyone. Harassment, fraud, unsafe behavior, and fake reviews are prohibited and can lead to removal. Report concerns and we’ll act.',
        ],
      },
      {
        id: 'tips',
        heading: 'Safety tips',
        body: [
          {
            list: [
              'Keep all bookings, payments, and messages on BestHand;',
              'Review a pro’s profile, ratings, and reviews before booking;',
              'Share job details and access instructions through the app;',
              'Report anything that feels wrong to ' + CONTACT.support + '.',
            ],
          },
        ],
      },
      {
        id: 'support',
        heading: 'Support when you need it',
        body: [
          'Our support team is here to help resolve issues fairly. Contact ' +
            CONTACT.support +
            ' or call ' +
            CONTACT.phone +
            '.',
        ],
      },
    ],
  },

  // ─────────────────────────────────────── CONTACT ───────────────────────────────────────
  'contact-us': {
    url: '/contact-us',
    eyebrow: 'Support',
    title: 'Contact BestHand',
    metaTitle: 'Contact Us — BestHand',
    metaDescription: 'Reach BestHand support and our team by email or phone.',
    updated: 'June 5, 2026',
    intro: 'We’re here to help. Reach our team using the options below.',
    sections: [
      {
        id: 'support',
        heading: 'Customer & pro support',
        body: [
          'For help with a booking, payment, or account, email ' +
            CONTACT.support +
            '. We typically respond within 24–48 hours.',
        ],
      },
      {
        id: 'phone',
        heading: 'By phone',
        body: ['Call or text us at ' + CONTACT.phone + '.'],
      },
      {
        id: 'business',
        heading: 'Business, legal & press',
        body: [
          'For partnerships, legal notices, or media inquiries, email ' + CONTACT.legal + '.',
        ],
      },
      {
        id: 'safety',
        heading: 'Safety concerns',
        body: [
          'If you have an urgent safety concern, contact local emergency services first, then report the issue to us at ' +
            CONTACT.support +
            ' so we can take action.',
        ],
      },
      {
        id: 'mail',
        heading: 'Mailing address',
        body: [LEGAL_ENTITY + ', ' + ADDRESS + ', United States.'],
      },
    ],
  },

  // ─────────────────────────────────────── ACCESSIBILITY ───────────────────────────────────────
  // A11Y (WCAG 2.1 AA): public accessibility statement. Describes our conformance
  // target, the steps taken, known limitations, and how to request help / report
  // a barrier. Linked from the footer (Legal column).
  accessibility: {
    url: '/accessibility',
    eyebrow: 'Accessibility',
    title: 'Accessibility Statement',
    metaTitle: 'Accessibility Statement — BestHand',
    metaDescription:
      'BestHand is committed to digital accessibility and aims to meet WCAG 2.1 Level AA. Learn what we have done, known limitations, and how to get help.',
    updated: 'June 25, 2026',
    intro:
      COMPANY +
      ' is committed to making our website and mobile apps accessible to everyone, including people with disabilities. We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA, the recognized standard for web accessibility.',
    sections: [
      {
        id: 'commitment',
        heading: 'Our commitment',
        body: [
          'We believe everyone should be able to find and book trusted local help. We treat accessibility as an ongoing effort and work to remove barriers as we identify them, building it into how we design and develop new features rather than treating it as an afterthought.',
        ],
      },
      {
        id: 'measures',
        heading: 'What we have done',
        body: [
          'Steps we have taken across our high-traffic public pages include:',
          { h: 'Keyboard navigation' },
          'A “Skip to main content” link lets keyboard and screen-reader users bypass the site header and jump straight to page content. Interactive controls are reachable and operable with a keyboard.',
          { h: 'Text alternatives' },
          'Meaningful images carry descriptive alternative text, and decorative images are marked so assistive technology can ignore them. Icon-only controls expose accessible labels.',
          { h: 'Structure and semantics' },
          'Pages use semantic landmarks (header, main, navigation, footer) and headings so screen-reader users can understand and move through the content.',
          { h: 'Readable, responsive design' },
          'Our pages reflow on small screens and support browser zoom, and we work toward sufficient color contrast for text and essential controls.',
        ],
      },
      {
        id: 'standard',
        heading: 'Conformance status',
        body: [
          'We are working toward full conformance with WCAG 2.1 Level AA. “Partially conformant” means some parts of the content may not yet fully meet the standard; we are actively addressing known gaps and continue to audit our pages and apps over time.',
        ],
      },
      {
        id: 'limitations',
        heading: 'Known limitations',
        body: [
          'Despite our efforts, some content may not yet be fully accessible. Known areas we are continuing to improve include certain complex interactive widgets, third-party embeds and payment elements we do not fully control, and a complete audit of every admin and account screen. If you encounter a barrier not listed here, please let us know — your feedback helps us prioritize.',
        ],
      },
      {
        id: 'feedback',
        heading: 'Feedback & getting help',
        body: [
          'We welcome your feedback on the accessibility of BestHand. If you encounter an accessibility barrier, or need information from this site in a different format, please contact us and we will work with you to provide the information or service you need:',
          'Email: ' + CONTACT.support,
          'Phone / text: ' + CONTACT.phone,
          'Mail: ' + LEGAL_ENTITY + ', ' + ADDRESS + ', United States.',
          'Please include the page or feature, the problem you experienced, and the browser or device you used. We aim to respond within 24–48 hours.',
        ],
      },
    ],
  },

  // ───────────────────────────────── LARGE PROJECTS TERMS (supplement) ─────────────────────────────────
  // LEGAL_REVIEW_REQUIRED: drafted for counsel review before production. Supplements
  // (does not replace) the main Terms of Service for large/custom home-improvement
  // projects booked through companies (escrow, milestones, lien waivers, HIC).
  'large-projects-terms': {
    url: '/large-projects-terms',
    eyebrow: 'Legal — Large Projects',
    title: 'Large Projects Terms',
    metaTitle: 'Large Projects Terms — BestHand',
    metaDescription:
      'Additional terms for large home-improvement projects booked through verified companies on BestHand: quotes, contracts, milestone escrow, lien waivers, licensing, and cancellation rights.',
    updated: 'June 29, 2026',
    intro:
      'These Large Projects Terms ("LP Terms") supplement, and are part of, the BestHand ' +
      'Terms of Service. They apply when a Customer books a large or custom home-improvement ' +
      'project ("Large Project") through a verified business ("Company"). If these LP Terms ' +
      'conflict with the general Terms for a Large Project, these LP Terms control for that project. ' +
      'Capitalized terms not defined here have the meaning given in the Terms of Service.',
    sections: [
      {
        id: 'lp-scope',
        heading: '1. Scope & relationship to the Terms of Service',
        body: [
          'Large Projects are larger, multi-step home-improvement or construction projects quoted and performed by a Company rather than booked as an individual small job. They use quotes, a written contract, and milestone-based payments held in escrow.',
          'Everything in the main Terms — including BestHand’s role as a neutral marketplace, the disclaimers, limitation of liability, assumption of risk, and the binding individual arbitration and class-action waiver — applies fully to Large Projects, as supplemented below.',
        ],
      },
      {
        id: 'lp-role',
        heading: '2. BestHand is the platform — not a party to the work',
        body: [
          'For every Large Project, the contract for the work is formed and exists solely and directly between the Customer and the Company. BestHand is not a contractor, builder, general contractor, construction manager, design professional, or party to that contract, and does not perform, supervise, schedule, inspect, or control any work.',
          'BestHand provides the technology to request projects, exchange quotes, generate the contract and statutory documents, hold and release milestone funds, and facilitate communication. Any verification, license/insurance status, rating, or badge shown is for convenience only and is not an endorsement, warranty, or guarantee.',
        ],
      },
      {
        id: 'lp-contract',
        heading: '3. The Home Improvement Contract',
        body: [
          'Before work begins, the Customer and Company enter into a written Home Improvement Contract generated through the Platform using the form applicable to the project’s state. That contract — including its scope, price, schedule, milestones, warranties, and required state notices and disclosures — governs the work and is between the Customer and the Company.',
          'Each party is responsible for reviewing the contract before signing. BestHand provides the document and the state-specific statutory language as a convenience and is not responsible for the parties’ performance of, or disputes under, the contract.',
        ],
      },
      {
        id: 'lp-escrow',
        heading: '4. Milestone escrow & release of funds',
        body: [
          'Large Projects are paid in milestones. The Customer’s milestone funds are collected and held by our payment processor (Stripe) and released to the Company when the milestone is approved by the Customer and the Company has signed the required lien waiver for that milestone.',
          'BestHand acts only as a technology platform facilitating payment and, where applicable, as a limited payment/escrow conduit through Stripe. BestHand does not take ownership of, lend, or invest project funds, and is not a bank, escrow company, or money transmitter; payment and any holding of funds are provided through Stripe under its agreements.',
          { h: 'Construction trust funds' },
          'In states with construction trust-fund laws, funds a Company receives for a project are trust assets and the Company (the contractor) is the trustee responsible for applying them first to the cost of the improvement (subcontractors, laborers, and materials) before any other use. BestHand is a conduit only and is not a trustee of those funds.',
          { h: 'Reversals' },
          'If a payment is refunded, charged back, disputed, or fails to clear, related lien waivers may be voided and releases re-evaluated, and BestHand may withhold, offset, or reverse amounts as described in the Terms.',
        ],
      },
      {
        id: 'lp-lien-waivers',
        heading: '5. Lien waivers',
        body: [
          'At each milestone (and at completion), the Company — and, where applicable, its subcontractors and suppliers — sign lien waivers using the form required by the project’s state. A lien waiver is a legal document by which the signer gives up certain rights to file a mechanic’s lien against the property for the amount covered.',
          'Lien waivers are exchanged between the Customer and the Company (and its subcontractors/suppliers). BestHand generates the correct statutory form as a convenience; it is not a party to the waiver and gives no legal advice. Consult your own professional about the effect of any waiver.',
        ],
      },
      {
        id: 'lp-licensing',
        heading: '6. Licensing, insurance & bonding',
        body: [
          'Where a state or locality requires a contractor license, insurance, bond, or workers’ compensation for the work, the Company is solely responsible for holding and maintaining them. BestHand may collect and display license/insurance information for convenience and may require it before a Company can quote, but does not guarantee its accuracy, validity, or sufficiency.',
          'You are responsible for confirming a Company’s licensing and insurance for your project and jurisdiction.',
        ],
      },
      {
        id: 'lp-cancellation',
        heading: '7. Down payment & cancellation rights',
        body: [
          'Down payments and milestone amounts follow the limits set by applicable state law and the contract. You may have a right to cancel within a statutory cooling-off period (for example, three business days for many home-solicitation/home-improvement contracts, and longer in certain cases such as for older adults or declared disasters). The applicable cancellation period and instructions are stated in your contract and the notice of cancellation provided with it.',
          'Cancellations, refunds, and any forfeiture or return of a down payment are governed by the contract and applicable law.',
        ],
      },
      {
        id: 'lp-change-orders',
        heading: '8. Changes & change orders',
        body: [
          'Changes to the scope, price, or schedule should be documented as a written change order through the Platform and agreed by both parties before the additional work proceeds. Unapproved changes are at the parties’ own risk.',
        ],
      },
      {
        id: 'lp-subcontractors',
        heading: '9. Subcontractors',
        body: [
          'A Company may use subcontractors to perform parts of a project. The Company remains fully responsible to the Customer for the entire project, for its subcontractors’ work and conduct, for paying them, and for ensuring they meet applicable licensing, insurance, and lien-waiver requirements. BestHand has no relationship with, and no responsibility for, subcontractors.',
        ],
      },
      {
        id: 'lp-disputes',
        heading: '10. Project & milestone disputes',
        body: [
          'If a Customer disputes a milestone, the Platform may pause release of the disputed funds while the parties work it out, and BestHand may offer optional, non-binding facilitation. BestHand does not adjudicate the merits of the underlying work and is not responsible for resolving contract disputes between Customer and Company.',
          'Any dispute between you and BestHand remains subject to the binding individual arbitration and class-action waiver in the Terms of Service.',
        ],
      },
      {
        id: 'lp-circumvention',
        heading: '11. Keep projects on the Platform',
        body: [
          'To protect both sides (escrow, documents, lien-waiver compliance, and support), Customers and Companies introduced through BestHand must keep the project, its payments, and its agreements on the Platform. Taking a Platform-sourced project off-platform to avoid fees or protections is prohibited and may result in suspension and the remedies described in the Terms.',
        ],
      },
    ],
  },

  // ───────────────────────────── COMPANY & CONTRACTOR AGREEMENT ─────────────────────────────
  // LEGAL_REVIEW_REQUIRED: drafted for counsel review before production.
  'company-terms': {
    url: '/company-terms',
    eyebrow: 'Legal — For Companies',
    title: 'Company & Contractor Agreement',
    metaTitle: 'Company & Contractor Agreement — BestHand',
    metaDescription:
      'The terms that apply to businesses and contractors using BestHand Large Projects: business verification (KYB), licensing and insurance, payouts via Stripe, lien waivers, subcontractors, and conduct.',
    updated: 'June 29, 2026',
    intro:
      'This Company & Contractor Agreement ("Company Agreement") applies to any business, ' +
      'contractor, or company ("Company," "you") that registers for or uses BestHand Large ' +
      'Projects. It supplements the Terms of Service and the Large Projects Terms. By onboarding ' +
      'a company, submitting quotes, or accepting projects, you agree to it on behalf of the business ' +
      'you represent, and you represent that you are authorized to do so.',
    sections: [
      {
        id: 'co-eligibility',
        heading: '1. Eligibility & business verification (KYB)',
        body: [
          'To offer Large Projects you must complete business verification ("KYB"). You authorize BestHand and its providers (including Stripe and Middesk) to collect and verify your business and ownership information — which may include your legal business name, EIN/tax ID, business address, beneficial-owner and representative identity details (including government identifiers and ID documents), and licensing, insurance, and bank information — and to re-verify it over time.',
          'You represent that all information you provide is true, accurate, and complete, and you will keep it current. We may approve, decline, suspend, or revoke access based on verification results.',
        ],
      },
      {
        id: 'co-licensing',
        heading: '2. Licensing, insurance, bonding & workers’ compensation',
        body: [
          'You represent and warrant that you hold, and will maintain for the life of each project, all licenses, registrations, insurance, bonds, and workers’ compensation coverage required by applicable federal, state, and local law for the work you perform and the jurisdictions where you perform it.',
          'You will provide proof on request and keep it current on the Platform. Verification by BestHand is for convenience and is not legal advice or a guarantee; compliance is solely your responsibility.',
        ],
      },
      {
        id: 'co-contracts',
        heading: '3. Quotes, contracts & performance',
        body: [
          'Quotes you submit are binding offers on the terms shown. When a Customer selects your quote, you and the Customer enter into the Home Improvement Contract generated through the Platform using the applicable state form, and you are responsible for performing the work in a good and workmanlike manner, on schedule, in compliance with the contract, applicable codes, permits, and law.',
          'You are responsible for obtaining required permits and for the accuracy of your pricing, scope, and disclosures.',
        ],
      },
      {
        id: 'co-payouts',
        heading: '4. Payouts, escrow & Stripe',
        body: [
          'Payouts are made through Stripe, subject to the Stripe Connected Account Agreement. Milestone funds are held and released as described in the Large Projects Terms — on Customer approval and your signed lien waiver for that milestone.',
          'You authorize BestHand to instruct release of milestone funds and to withhold, offset, delay, or reverse payouts to address refunds, chargebacks, disputes, fraud, reversed or dishonored payments, or violations of these terms.',
        ],
      },
      {
        id: 'co-lien-trust',
        heading: '5. Lien waivers & construction trust funds',
        body: [
          'You agree to sign the required state lien-waiver forms at each milestone and at completion, and to obtain corresponding waivers from your subcontractors and suppliers where applicable. You will not file or threaten a lien for amounts that have been paid and waived.',
          'In states with construction trust-fund laws, you are the trustee of funds you receive for a project and must apply them first to the cost of the improvement (subcontractors, laborers, materials, and related taxes/benefits) before any other use, and keep the records the law requires.',
        ],
      },
      {
        id: 'co-subs',
        heading: '6. Subcontractors',
        body: [
          'You may use subcontractors but remain fully responsible to the Customer and to BestHand for the entire project, including your subcontractors’ work, conduct, licensing, insurance, payment, and lien waivers. Your subcontractors must not contact or solicit the Customer off-platform.',
        ],
      },
      {
        id: 'co-conduct',
        heading: '7. Conduct, anti-circumvention & non-solicitation',
        body: [
          'You will deal honestly and professionally, keep client identities and project details confidential, and keep Platform-sourced projects, payments, and agreements on the Platform. You will not take a Platform-sourced Customer or project off-platform to avoid fees or protections, and will not misuse client contact information.',
          'Reviews and advertising must be truthful and comply with applicable law.',
        ],
      },
      {
        id: 'co-tax',
        heading: '8. Independent status & taxes',
        body: [
          'You are an independent business, not an employee, agent, partner, or joint venturer of BestHand. You are solely responsible for your taxes, your personnel, and your business operations. Nothing here creates any agency or employment relationship.',
        ],
      },
      {
        id: 'co-termination',
        heading: '9. Suspension, deactivation & account deletion',
        body: [
          'We may suspend, limit, or deactivate your access at our discretion, including for failed verification, expired or insufficient licensing/insurance, disputes, chargebacks, or violations. You may request deletion of your company account; deletion is not available while a project is active, and certain records may be retained as required by law or these terms.',
        ],
      },
      {
        id: 'co-liability',
        heading: '10. Indemnification, liability & arbitration',
        body: [
          'You will indemnify and hold harmless BestHand and its affiliates from claims arising out of your work, your subcontractors, your representations, or your violation of law or these terms. BestHand’s disclaimers and limitation of liability in the Terms of Service apply to you.',
          'Disputes between you and BestHand are subject to the binding individual arbitration and class-action waiver in the Terms of Service.',
        ],
      },
    ],
  },
};

// Aliases so old/duplicate footer URLs still resolve to the right canonical page.
const ALIASES = {
  '/terms-of-service': 'terms',
  '/privacy': 'privacy-policy',
  // COMPLIANCE: Google Play flagged /partner/privacy-policy as "not a valid privacy policy page" —
  // it rendered a near-empty vendor CMS stub (~743 chars). The pro app opens this URL
  // (constant.dart privacyPolicyURL), so the URL must stay alive; alias it to the counsel-approved
  // premium policy instead of the stub.
  '/partner/privacy-policy': 'privacy-policy',
};

export function getLegalPage(pathname) {
  if (!pathname) return null;
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (ALIASES[clean]) return PAGES[ALIASES[clean]] || null;
  const key = clean.replace(/^\//, '');
  return PAGES[key] || null;
}

export const LEGAL_PATHS = [
  ...Object.values(PAGES).map((p) => p.url),
  ...Object.keys(ALIASES),
];

export default PAGES;
